import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchFolderById,
  fetchFolderChildren,
  fetchFoldersByProject,
  updateFolder,
  deleteFolder,
  FolderVisibility,
} from '../../../store/folderSlice';
import { taskAPI, folderAPI, featureAPI } from '../../../api/services';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const visibilityLabel: Record<FolderVisibility, string> = {
  private: 'Private',
  public_org: 'Org-wide',
  public: 'Public',
  custom: 'Custom',
};

const visibilityColor: Record<FolderVisibility, string> = {
  private: 'bg-gray-100 text-gray-600',
  public_org: 'bg-blue-50 text-blue-700',
  public: 'bg-green-50 text-green-700',
  custom: 'bg-purple-50 text-purple-700',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125L18 8.625" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

interface EmptyStateProps {
  label: string;
  actionLabel?: string;
  onAction?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ label, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
      </svg>
    </div>
    <p className="text-sm text-gray-500">{label}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="mt-2 text-sm text-blue-600 hover:underline font-medium"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  folder: {
    _id: string;
    name: string;
    description?: string;
    visibility: FolderVisibility;
    startDate?: string;
    estimatedEndDate?: string;
  };
  onClose: () => void;
  onSaved: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ folder, onClose, onSaved }) => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector(state => state.folders);
  const [form, setForm] = useState({
    name: folder.name,
    description: folder.description ?? '',
    visibility: folder.visibility,
    startDate: folder.startDate ? folder.startDate.slice(0, 10) : '',
    estimatedEndDate: folder.estimatedEndDate ? folder.estimatedEndDate.slice(0, 10) : '',
    amendmentReason: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(
      updateFolder({
        id: folder._id,
        data: {
          name: form.name,
          description: form.description || undefined,
          visibility: form.visibility,
          startDate: form.startDate || undefined,
          estimatedEndDate: form.estimatedEndDate || undefined,
          amendmentReason: form.amendmentReason || undefined,
        },
      })
    );
    if (updateFolder.fulfilled.match(result)) {
      onSaved();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Folder</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={form.estimatedEndDate}
                onChange={e => setForm(f => ({ ...f, estimatedEndDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
            <select
              value={form.visibility}
              onChange={e => setForm(f => ({ ...f, visibility: e.target.value as FolderVisibility }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="private">Private</option>
              <option value="public_org">Org-wide</option>
              <option value="public">Public</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for change (optional)</label>
            <input
              value={form.amendmentReason}
              onChange={e => setForm(f => ({ ...f, amendmentReason: e.target.value }))}
              placeholder="Briefly describe what changed"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Card components ──────────────────────────────────────────────────────────

const SubfolderCard: React.FC<{ folder: any; onClick: () => void }> = ({ folder, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all"
  >
    <div className="flex items-center gap-2 mb-1">
      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
      <span className="text-sm font-medium text-gray-900 truncate">{folder.name}</span>
    </div>
    {folder.description && (
      <p className="text-xs text-gray-500 truncate">{folder.description}</p>
    )}
    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
      {folder.stats?.totalChildren != null && (
        <span>{folder.stats.totalChildren} items</span>
      )}
      {folder.estimatedEndDate && (
        <span>Due {formatDate(folder.estimatedEndDate)}</span>
      )}
    </div>
  </button>
);

const TaskCard: React.FC<{ task: any }> = ({ task }) => {
  const statusColors: Record<string, string> = {
    todo: 'bg-gray-100 text-gray-600',
    in_progress: 'bg-blue-50 text-blue-700',
    done: 'bg-green-50 text-green-700',
    blocked: 'bg-red-50 text-red-600',
  };
  const status = task.status ?? 'todo';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">{task.title}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[status] ?? 'bg-gray-100 text-gray-600'}`}>
          {status.replace('_', ' ')}
        </span>
      </div>
      {task.dueDate && (
        <p className="text-xs text-gray-400 mt-1">Due {formatDate(task.dueDate)}</p>
      )}
    </div>
  );
};

const FeatureCard: React.FC<{ feature: any; onClick?: () => void }> = ({ feature, onClick }) => {
  const statusColors: Record<string, string> = {
    planning: 'bg-gray-100 text-gray-600',
    development: 'bg-blue-50 text-blue-700',
    testing: 'bg-purple-50 text-purple-700',
    done: 'bg-green-50 text-green-700',
    deployed: 'bg-teal-50 text-teal-700',
    archived: 'bg-gray-200 text-gray-400',
  };
  const approvalColors: Record<string, string> = {
    pending_approval: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const status = feature.status ?? 'planning';
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">{feature.title ?? feature.name}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[status] ?? 'bg-gray-100 text-gray-600'}`}>
          {status.replace('_', ' ')}
        </span>
      </div>
      {feature.approvalStatus && feature.approvalStatus !== 'not_required' && (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${approvalColors[feature.approvalStatus] || ''}`}>
          {feature.approvalStatus.replace('_', ' ')}
        </span>
      )}
      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
        {feature.stats && <span>{feature.stats.completedTasks}/{feature.stats.totalTasks} tasks</span>}
        {feature.storyPoints > 0 && <span className="text-blue-600">{feature.storyPoints} SP</span>}
        {(feature.dates?.estimatedEndDate || feature.estimatedEndDate) && (
          <span>Due {formatDate(feature.dates?.estimatedEndDate || feature.estimatedEndDate)}</span>
        )}
      </div>
    </div>
  );
};

// ─── FolderDetail ─────────────────────────────────────────────────────────────

type TabKey = 'subfolders' | 'tasks' | 'features';

interface FolderDetailProps {
  folderId: string;
  projectId: string;
  onNavigateToFolder?: (folderId: string) => void;
  onSelectFeature?: (featureId: string) => void;
}

const FolderDetail: React.FC<FolderDetailProps> = ({ folderId, projectId, onNavigateToFolder, onSelectFeature }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentFolder, children, loading, error } = useAppSelector(state => state.folders);
  const [activeTab, setActiveTab] = useState<TabKey>('subfolders');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInlineTask, setShowInlineTask] = useState(false);
  const [inlineTaskTitle, setInlineTaskTitle] = useState('');
  const [inlineTaskDesc, setInlineTaskDesc] = useState('');
  const [inlineTaskPriority, setInlineTaskPriority] = useState('medium');
  const [inlineTaskCreating, setInlineTaskCreating] = useState(false);
  const [showInlineFeature, setShowInlineFeature] = useState(false);
  const [inlineFeatureName, setInlineFeatureName] = useState('');
  const [inlineFeatureDesc, setInlineFeatureDesc] = useState('');
  const [inlineFeaturePriority, setInlineFeaturePriority] = useState('medium');
  const [inlineFeatureVisibility, setInlineFeatureVisibility] = useState('public_org');
  const [inlineFeatureWeight, setInlineFeatureWeight] = useState(10);
  const [inlineFeatureSP, setInlineFeatureSP] = useState(0);
  const [inlineFeatureCreating, setInlineFeatureCreating] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);

  const handleInlineTaskCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineTaskTitle.trim()) return;
    setInlineTaskCreating(true);
    try {
      await taskAPI.createTask({
        title: inlineTaskTitle,
        description: inlineTaskDesc || inlineTaskTitle,
        parentType: 'folder',
        parentId: folderId,
        projectId,
        priority: inlineTaskPriority,
        status: 'todo',
      } as any);
      setInlineTaskTitle('');
      setInlineTaskDesc('');
      setShowInlineTask(false);
      dispatch(fetchFolderChildren(folderId));
    } catch { /* error handled silently */ }
    setInlineTaskCreating(false);
  };

  const handleInlineFeatureCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineFeatureName.trim()) return;
    setInlineFeatureCreating(true);
    try {
      await featureAPI.create({
        name: inlineFeatureName,
        description: inlineFeatureDesc || inlineFeatureName,
        projectId,
        folderId,
        priority: inlineFeaturePriority as any,
        visibility: inlineFeatureVisibility as any,
        weight: inlineFeatureWeight,
        storyPoints: inlineFeatureSP,
      } as any);
      setInlineFeatureName('');
      setInlineFeatureDesc('');
      setInlineFeatureWeight(10);
      setInlineFeatureSP(0);
      setShowInlineFeature(false);
      dispatch(fetchFolderChildren(folderId));
    } catch { /* error handled silently */ }
    setInlineFeatureCreating(false);
  };

  useEffect(() => {
    if (folderId) {
      dispatch(fetchFolderById(folderId));
      dispatch(fetchFolderChildren(folderId));
    }
  }, [folderId, dispatch]);

  const handleConvertToFeature = async () => {
    setConverting(true);
    try {
      await folderAPI.convertToFeature(folderId);
      setShowConvertModal(false);
      // Refresh folder list to remove the converted folder
      dispatch(fetchFoldersByProject(projectId));
      onNavigateToFolder?.(null as any);
    } catch {
      /* error */
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this folder? This action cannot be undone.')) return;
    const result = await dispatch(deleteFolder(folderId));
    if (deleteFolder.fulfilled.match(result)) {
      navigate(-1);
    }
  };

  if (loading && !currentFolder) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-400">
        Loading...
      </div>
    );
  }

  if (error && !currentFolder) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (!currentFolder) return null;

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'subfolders', label: 'Subfolders', count: children?.folders.length ?? 0 },
    { key: 'tasks', label: 'Tasks', count: children?.tasks.length ?? 0 },
    { key: 'features', label: 'Features', count: children?.features.length ?? 0 },
  ];

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Breadcrumb */}
        {currentFolder.hierarchyPathNames.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-3 flex-wrap">
            <span className="hover:text-gray-600 cursor-pointer">{currentFolder.projectName ?? 'Project'}</span>
            {currentFolder.hierarchyPathNames.map((seg, i) => (
              <React.Fragment key={i}>
                <ChevronRight />
                <span
                  className={
                    i === currentFolder.hierarchyPathNames.length - 1
                      ? 'text-gray-700 font-medium'
                      : 'hover:text-gray-600 cursor-pointer'
                  }
                >
                  {seg}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Title row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 leading-tight">{currentFolder.name}</h1>
            {currentFolder.description && (
              <p className="text-sm text-gray-500 mt-1">{currentFolder.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${visibilityColor[currentFolder.visibility]}`}>
              {visibilityLabel[currentFolder.visibility]}
            </span>
            <button
              onClick={() => setShowConvertModal(true)}
              className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-700 border border-gray-200 hover:border-purple-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
              Convert to Feature
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 border border-gray-200 hover:border-blue-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              <EditIcon />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-sm text-red-500 hover:text-red-700 border border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-400">
          {currentFolder.startDate && <span>Start: {formatDate(currentFolder.startDate)}</span>}
          {currentFolder.estimatedEndDate && <span>Due: {formatDate(currentFolder.estimatedEndDate)}</span>}
          {currentFolder.createdByName && <span>Created by {currentFolder.createdByName}</span>}
          <span>{currentFolder.stats.totalChildren} items</span>
        </div>
      </div>

      {/* Content card */}
      <div className="bg-white rounded-xl border border-gray-200">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {/* Subfolders */}
          {activeTab === 'subfolders' && (
            <>
              {children?.folders.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {children.folders.map(f => (
                    <SubfolderCard
                      key={f._id}
                      folder={f}
                      onClick={() => onNavigateToFolder?.(f._id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState label="No subfolders yet." />
              )}
            </>
          )}

          {/* Tasks */}
          {activeTab === 'tasks' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{children?.tasks.length ?? 0} tasks</span>
                <button
                  onClick={() => setShowInlineTask(!showInlineTask)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {showInlineTask ? 'Cancel' : '+ Add task'}
                </button>
              </div>
              {showInlineTask && (
                <form onSubmit={handleInlineTaskCreate} className="bg-white rounded-xl border border-gray-200 p-4 mb-3 space-y-2">
                  <input
                    value={inlineTaskTitle}
                    onChange={e => setInlineTaskTitle(e.target.value)}
                    placeholder="Task title *"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <textarea
                    value={inlineTaskDesc}
                    onChange={e => setInlineTaskDesc(e.target.value)}
                    placeholder="Description (optional)"
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2">
                    <select
                      value={inlineTaskPriority}
                      onChange={e => setInlineTaskPriority(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <button
                      type="submit"
                      disabled={inlineTaskCreating || !inlineTaskTitle.trim()}
                      className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {inlineTaskCreating ? 'Creating…' : 'Create'}
                    </button>
                  </div>
                </form>
              )}
              {children?.tasks.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {children.tasks.map((t: any) => (
                    <TaskCard key={t._id} task={t} />
                  ))}
                </div>
              ) : !showInlineTask ? (
                <EmptyState
                  label="No tasks in this folder."
                  actionLabel="Add task"
                  onAction={() => setShowInlineTask(true)}
                />
              ) : null}
            </>
          )}

          {/* Features */}
          {activeTab === 'features' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{children?.features.length ?? 0} features</span>
                <button
                  onClick={() => setShowInlineFeature(!showInlineFeature)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {showInlineFeature ? 'Cancel' : '+ Add feature'}
                </button>
              </div>
              {showInlineFeature && (
                <form onSubmit={handleInlineFeatureCreate} className="bg-white rounded-xl border border-gray-200 p-5 mb-3 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">New Feature</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      value={inlineFeatureName}
                      onChange={e => setInlineFeatureName(e.target.value)}
                      placeholder="Feature name"
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={inlineFeatureDesc}
                      onChange={e => setInlineFeatureDesc(e.target.value)}
                      placeholder="What does this feature deliver?"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={inlineFeaturePriority}
                        onChange={e => setInlineFeaturePriority(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Visibility</label>
                      <select
                        value={inlineFeatureVisibility}
                        onChange={e => setInlineFeatureVisibility(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="private">Private</option>
                        <option value="public_org">Organisation</option>
                        <option value="public">Public</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Weight (0-100)</label>
                      <input
                        type="number" min={0} max={100}
                        value={inlineFeatureWeight}
                        onChange={e => setInlineFeatureWeight(Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Story Points</label>
                      <input
                        type="number" min={0}
                        value={inlineFeatureSP}
                        onChange={e => setInlineFeatureSP(Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={() => setShowInlineFeature(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                    <button
                      type="submit"
                      disabled={inlineFeatureCreating || !inlineFeatureName.trim()}
                      className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {inlineFeatureCreating ? 'Creating…' : 'Create Feature'}
                    </button>
                  </div>
                </form>
              )}
              {children?.features.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {children.features.map((f: any) => (
                    <FeatureCard key={f._id} feature={f} onClick={() => onSelectFeature?.(f._id)} />
                  ))}
                </div>
              ) : !showInlineFeature ? (
                <EmptyState
                  label="No features in this folder."
                  actionLabel="Add feature"
                  onAction={() => setShowInlineFeature(true)}
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      {showEditModal && currentFolder && (
        <EditModal
          folder={currentFolder}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            dispatch(fetchFolderById(folderId));
          }}
        />
      )}

      {showConvertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Convert to Feature</h3>
            <p className="text-sm text-gray-500 mb-6">
              Convert this folder to a feature? All tasks will be moved under the new feature. The folder will be deleted.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowConvertModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToFeature}
                disabled={converting}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
              >
                {converting ? 'Converting...' : 'Convert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderDetail;
